import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMEventAllList from "../../features/university-manager/UMEventAllList";
import UMEventForm from "../../features/university-manager/UMEventForm";
import UMEventTicketAttendants from "../../features/university-manager/UMEventTicketAttendants";
import EventDetailModal from "../../components/common/EventDetailModal";
import AppButton from "../../components/ui/AppButton";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import { useNotification } from "../../context/NotificationContext";
import umService from "../../services/university-admin/umService";

const UMAllEvents = () => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();
  const { userRole, currentTenantId } = useTenant();
  const { showSuccess, showError } = useNotification();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubFilter, setClubFilter] = useState("");
  const [eventStatusFilter, setEventStatusFilter] = useState("");
  const [campusFilter, setCampusFilter] = useState("");
  const [statusTab, setStatusTab] = useState(true);
  const [clubs, setClubs] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [viewingEvent, setViewingEvent] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [managingTicketAttendants, setManagingTicketAttendants] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchClubs = useCallback(async () => {
    if (!can(PERMISSIONS.club.list)) return;
    const res = await umService.getAllClubs({ userRole, currentTenantId });
    if (res.success) setClubs(res.data);
  }, [userRole, currentTenantId, can]);

  const fetchCampuses = useCallback(async () => {
    if (!can(PERMISSIONS.campus.list)) return;
    const res = await umService.getAllCampuses({ userRole, currentTenantId });
    if (res.success) setCampuses(res.data);
  }, [userRole, currentTenantId, can]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const res = await umService.getEvents({
      userRole,
      currentTenantId,
      eventStatus: eventStatusFilter || undefined,
      clubId: clubFilter || undefined,
      campusId: campusFilter || undefined,
      status: statusTab,
      page: currentPage,
      size: pageSize,
    });
    if (res.success) {
      setEvents(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    } else {
      setEvents([]);
      setTotalPages(0);
      setTotalElements(0);
    }
    setLoading(false);
  }, [userRole, currentTenantId, clubFilter, eventStatusFilter, campusFilter, statusTab, currentPage, pageSize]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, refreshTrigger]);

  useEffect(() => {
    fetchClubs();
    fetchCampuses();
  }, [fetchClubs, fetchCampuses]);

  const handleView = async (event) => {
    try {
      setLoading(true);
      const response = await umService.getEventById(event.id);
      if (response.success) {
        setViewingEvent(response.data);
      } else {
        showError(t("events.allEvents.messages.fetchFailed", { defaultValue: "Failed to fetch event details." }));
        setViewingEvent(event); // fallback to list item
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
      showError(t("events.allEvents.messages.fetchFailed", { defaultValue: "Failed to fetch event details." }));
      setViewingEvent(event); // fallback to list item
    } finally {
      setLoading(false);
    }
  };
  const handleCloseView = () => setViewingEvent(null);

  const handleCreate = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEdit = async (event) => {
    setViewingEvent(null);
    try {
      setFormLoading(true);
      const response = await umService.getEventById(event.id);
      setEditingEvent(response.success ? response.data : event);
      setShowForm(true);
    } catch (error) {
      console.error("Error fetching event details:", error);
      setEditingEvent(event);
      setShowForm(true);
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleFormSubmit = async (payload) => {
    try {
      setFormLoading(true);
      const response = editingEvent
        ? await umService.updateEvent({ id: editingEvent.id, data: payload })
        : await umService.createEvent({ data: payload });

      if (response.success) {
        showSuccess(editingEvent ? t("events.allEvents.messages.updateSuccess") : t("events.allEvents.messages.createSuccess"));
        setShowForm(false);
        setEditingEvent(null);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(editingEvent ? t("events.allEvents.messages.updateFailed") : t("events.allEvents.messages.createFailed"));
      }
    } catch (error) {
      console.error("Event form submission error:", error);
      showError(editingEvent ? t("events.allEvents.messages.updateFailed") : t("events.allEvents.messages.createFailed"));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelEvent = async (event) => {
    if (!window.confirm(t("events.allEvents.messages.confirmCancel"))) return;
    try {
      setActionLoadingId(event.id);
      const response = await umService.cancelEvent(event.id);
      if (response.success) {
        showSuccess(t("events.allEvents.messages.cancelSuccess"));
        setViewingEvent(null);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(t("events.allEvents.messages.cancelFailed"));
      }
    } catch (error) {
      console.error("Error cancelling event:", error);
      showError(t("events.allEvents.messages.cancelFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!window.confirm(t("events.allEvents.messages.confirmDelete"))) return;
    try {
      setActionLoadingId(event.id);
      const response = await umService.deleteEvent(event.id);
      if (response.success) {
        showSuccess(t("events.allEvents.messages.deleteSuccess"));
        setViewingEvent(null);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(t("events.allEvents.messages.deleteFailed"));
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      showError(t("events.allEvents.messages.deleteFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReactivateEvent = async (event) => {
    if (!window.confirm(t("events.allEvents.messages.confirmReactivate"))) return;
    try {
      setActionLoadingId(event.id);
      const response = await umService.updateEvent({ id: event.id, data: { status: true } });
      if (response.success) {
        showSuccess(t("events.allEvents.messages.reactivateSuccess"));
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(t("events.allEvents.messages.reactivateFailed"));
      }
    } catch (error) {
      console.error("Error reactivating event:", error);
      showError(t("events.allEvents.messages.reactivateFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleManageTicketAttendants = (event) => {
    setViewingEvent(null);
    setManagingTicketAttendants(event);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<AppButton key={i} variant={i === currentPage ? "primary" : "outline"} className="btn-sm mx-1" onClick={() => setCurrentPage(i)}>{i + 1}</AppButton>);
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">{tCommon("pagination.showingEntries", { start: currentPage * pageSize + 1, end: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements })}</div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>{tCommon("buttons.previous")}</AppButton>
          {pages}
          <AppButton variant="outline" className="btn-sm ms-2" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>{tCommon("buttons.next")}</AppButton>
        </div>
      </div>
    );
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.event.list)}>
      <div className="all-events-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("events.allEvents.title")}</h1>
            <p className="text-muted mb-0">{t("events.allEvents.subtitle")}</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0">{t("events.allEvents.show")}:</label>
            <select className="form-select form-select-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }} style={{ width: "auto" }}>
              <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
            </select>
          </div>
        </div>
        <UMEventAllList
          events={events}
          loading={loading}
          totalElements={totalElements}
          clubs={clubs}
          campuses={campuses}
          clubFilter={clubFilter}
          eventStatusFilter={eventStatusFilter}
          campusFilter={campusFilter}
          statusTab={statusTab}
          actionLoadingId={actionLoadingId}
          onClubFilterChange={(v) => { setClubFilter(v); setCurrentPage(0); }}
          onEventStatusFilterChange={(v) => { setEventStatusFilter(v); setCurrentPage(0); }}
          onCampusFilterChange={(v) => { setCampusFilter(v); setCurrentPage(0); }}
          onStatusTabChange={(v) => { setStatusTab(v); setCurrentPage(0); }}
          onClearFilters={() => { setClubFilter(""); setEventStatusFilter(""); setCampusFilter(""); setStatusTab(true); setCurrentPage(0); }}
          onCreate={handleCreate}
          onView={handleView}
          onEdit={handleEdit}
          onCancel={handleCancelEvent}
          onDelete={handleDeleteEvent}
          onReactivate={handleReactivateEvent}
          onManageTicketAttendants={handleManageTicketAttendants}
        />
        {renderPagination()}
      </div>
      </PermissionGuard>

      {/* Event Detail Modal */}
      {viewingEvent && (
        <EventDetailModal
          event={viewingEvent}
          onClose={handleCloseView}
          onEdit={can(PERMISSIONS.event.update) ? handleEdit : undefined}
          onCancel={can(PERMISSIONS.event.cancel) ? handleCancelEvent : undefined}
          onDelete={can(PERMISSIONS.event.delete) ? handleDeleteEvent : undefined}
          onManageTicketAttendants={can(PERMISSIONS.event.attendantList) ? handleManageTicketAttendants : undefined}
        />
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`fas ${editingEvent ? "fa-edit" : "fa-plus"} me-2`}></i>
                  {editingEvent ? t("events.allEvents.form.editEvent") : t("events.allEvents.form.addNewEvent")}
                </h5>
                <button type="button" className="btn-close" onClick={handleFormCancel} disabled={formLoading}></button>
              </div>
              <div className="modal-body">
                <UMEventForm event={editingEvent} onSubmit={handleFormSubmit} onCancel={handleFormCancel} loading={formLoading} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Attendants Modal */}
      {managingTicketAttendants && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-id-badge me-2"></i>
                  {t("events.ticketAttendants.title")}
                </h5>
                <button type="button" className="btn-close" onClick={() => setManagingTicketAttendants(null)}></button>
              </div>
              <div className="modal-body">
                <UMEventTicketAttendants event={managingTicketAttendants} />
              </div>
            </div>
          </div>
        </div>
      )}
    </UniversityManagerLayout>
  );
};

export default UMAllEvents;
